import React, { memo, useCallback, useMemo } from 'react'

import { Button, TextInput } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  setSizeUnit, setWholePrintSize, setLargestPrintSize, setSizePreset, clearSize,
} from 'Pods/SearchPage/Store/actions/size'
import { SIZE_AXIS, SIZE_PRESET, SIZE_UNIT } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import { hasAnyDimension } from 'Pods/SearchPage/Store/selectors'

const AXES = [
  { axis: SIZE_AXIS.WIDTH, labelKey: 'width' },
  { axis: SIZE_AXIS.DEPTH, labelKey: 'depth' },
  { axis: SIZE_AXIS.HEIGHT, labelKey: 'height' },
]

const UNIT_OPTIONS = [SIZE_UNIT.CM, SIZE_UNIT.IN, SIZE_UNIT.MM]

const PRESETS = [
  SIZE_PRESET.UNDER_5,
  SIZE_PRESET.RANGE_5_15,
  SIZE_PRESET.RANGE_15_30,
  SIZE_PRESET.RANGE_30_50,
  SIZE_PRESET.RANGE_50_100,
  SIZE_PRESET.OVER_100,
]

// Dimension inputs are max-constraints per axis ('' clears the axis); the preset→filter and
// dimension→filter query semantics are finalized with the backend subtask (see CU-86caebn34).
const handleDimensionChange = (setter, axis, rawValue) => {
  if (rawValue === '') {
    setter(axis, '')
    return
  }
  const numValue = parseInt(rawValue, 10)
  if (!Number.isNaN(numValue) && numValue >= 0) {
    setter(axis, numValue)
  }
}

function SizeContent({ hideReset = false }) {
  const t = useTranslate()

  const sizeUnit = useSearchPageStore(state => state.sizeUnit)
  const wholePrintSize = useSearchPageStore(state => state.wholePrintSize)
  const largestPrintSize = useSearchPageStore(state => state.largestPrintSize)
  const sizePreset = useSearchPageStore(state => state.sizePreset)

  const unitOptions = useMemo(() => UNIT_OPTIONS.map(value => ({
    value,
    label: t(`search_page.filters.size.units.${value}`),
  })), [t])

  const dimensionGroups = useMemo(() => [
    { labelKey: 'whole_print_size', dimensions: wholePrintSize, setter: setWholePrintSize },
    { labelKey: 'largest_print_size', dimensions: largestPrintSize, setter: setLargestPrintSize },
  ], [wholePrintSize, largestPrintSize])

  const hasSelectedAnyFilters = hasAnyDimension(wholePrintSize)
    || hasAnyDimension(largestPrintSize)
    || sizePreset !== undefined

  const handleUnitChange = useCallback((e) => setSizeUnit(e.target.value), [])

  const handlePresetToggle = useCallback((preset) => {
    setSizePreset(sizePreset === preset ? undefined : preset)
  }, [sizePreset])

  return (
    <div className="size-filter">
      <div className="size-filter__group">
        <label className="size-filter__group-label cgt-overline-md" htmlFor="size-unit">
          {t('search_page.filters.size.unit')}
        </label>
        {/* Native select on purpose: the shared selects (Triangle LightSelect, SelectField,
            FastSelect) are all react-select v1, whose stylesheet is not loaded on the search
            (fast) page, so they render unstyled here. Native is robust, accessible and the
            better mobile control. */}
        <select
          id="size-unit"
          name="size-unit"
          className="size-filter__unit cgt-body-base"
          value={sizeUnit}
          onChange={handleUnitChange}
        >
          {unitOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      {dimensionGroups.map(({ labelKey, dimensions, setter }) => (
        <React.Fragment key={labelKey}>
          <div className="cgt-divider" aria-hidden="true" />
          <div className="size-filter__group">
            <div className="size-filter__group-label cgt-overline-md">
              {t(`search_page.filters.size.${labelKey}`)}
            </div>
            <div className="size-filter__row">
              {AXES.map(({ axis, labelKey: axisLabelKey }) => (
                <TextInput
                  key={axis}
                  isPilot
                  id={`size-${labelKey}-${axis}`}
                  label={t(`search_page.filters.size.${axisLabelKey}`)}
                  inputType="number"
                  size="tiny"
                  type="primary"
                  step="1"
                  placeholder="0"
                  value={dimensions[axis] ?? ''}
                  onChange={(e) => handleDimensionChange(setter, axis, e.target.value)}
                  wrapperClassName="size-filter__input"
                />
              ))}
            </div>
          </div>
        </React.Fragment>
      ))}
      <div className="cgt-divider" aria-hidden="true" />
      <div className="size-filter__group">
        <div className="size-filter__group-label cgt-overline-md">
          {t('search_page.filters.size.quick_select')}
        </div>
        <div
          className="size-filter__presets"
          role="group"
          aria-label={t('search_page.filters.size.quick_select')}
        >
          {PRESETS.map(preset => (
            <Button
              key={preset}
              type="secondary"
              size="tiny"
              isActive={sizePreset === preset}
              aria-pressed={sizePreset === preset}
              onClick={() => handlePresetToggle(preset)}
              customClass="size-filter__preset"
            >
              {t(`search_page.filters.size.presets.${preset}`)}
            </Button>
          ))}
        </div>
      </div>
      {!hideReset && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
          onClick={clearSize}
          disabled={!hasSelectedAnyFilters}
        >
          {t('search_page.filters.size.reset')}
        </Button>
      )}
    </div>
  )
}

SizeContent.propTypes = {
  hideReset: bool,
}

export default memo(SizeContent)
